import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Responsive, WidthProvider } from "react-grid-layout";
import { subscribeWorkspace, subscribeSettings, saveCorkboardLayout, moveText, reorderTexts } from "../../services/workspaceService";
import { toSortedArray } from "../../services/workspaceService";
import HomeShell from "../shell/HomeShell";
import { BACKGROUNDS } from "../../config/backgrounds";
import { useEditLock } from "../../context/EditLockContext";
import deskGlass from "../../styles/deskGlass";
import { navigateToText } from "../../utils/textRoutes";
import { rememberWritingText } from "../../utils/writingSession";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const glass = deskGlass;

const ResponsiveGridLayout = WidthProvider(Responsive);

export default function VisionBoard() {
  const navigate = useNavigate();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [workspace, setWorkspace] = useState({ desks: [] });
  const [settings, setSettings] = useState({});
  const [allTexts, setAllTexts] = useState([]);
  const [filterMode, setFilterMode] = useState("all"); // "all" | "actionable" | "pinned"

  const [hierarchyMode, setHierarchyMode] = useState(false); // drawer → notebooks → notes
  const [activeHierarchyDrawerId, setActiveHierarchyDrawerId] = useState(null);

  useEffect(() => {
    const unsubWorkspace = subscribeWorkspace((data) => {
      setWorkspace(data);
    });
    const unsubSettings = subscribeSettings((data) => {
      setSettings(data);
    });
    return () => {
      unsubWorkspace();
      unsubSettings();
    };
  }, []);

  // Flatten all texts with notebook colors
  useEffect(() => {
    const texts = [];
    for (const desk of workspace?.desks || []) {
      for (const drawer of toSortedArray(desk.drawers)) {
        for (const notebook of toSortedArray(drawer.notebooks)) {
          for (const text of toSortedArray(notebook.texts)) {
            texts.push({
              ...text,
              deskId: desk.id,
              deskName: desk.name,
              drawerId: drawer.id,
              drawerName: drawer.name,
              notebookColor: notebook.color,
              notebookName: notebook.name,
              deskSlug: desk.slug,
              drawerSlug: drawer.slug,
              notebookId: notebook.id,
              notebookSlug: notebook.slug,
            });
          }
        }
      }
    }
    texts.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    setAllTexts(texts);
  }, [workspace]);

  const filteredTexts = filterMode === "pinned" ? allTexts.filter(t => t.isPinned) :
                       filterMode === "actionable" ? allTexts.filter(t => (t.tags || []).length > 0 || !t.isRead) :
                       allTexts;

  const drawerOptions = React.useMemo(() => {
    const map = new Map();
    for (const t of allTexts) {
      if (!t?.drawerId) continue;
      if (!map.has(t.drawerId)) {
        map.set(t.drawerId, { deskId: t.deskId, deskName: t.deskName, drawerId: t.drawerId, drawerName: t.drawerName });
      }
    }
    return [...map.values()];
  }, [allTexts]);

  React.useEffect(() => {
    if (!hierarchyMode) return;
    if (!drawerOptions.length) return;
    if (!activeHierarchyDrawerId) {
      setActiveHierarchyDrawerId(drawerOptions[0].drawerId);
      return;
    }
    if (!drawerOptions.some((d) => d.drawerId === activeHierarchyDrawerId)) {
      setActiveHierarchyDrawerId(drawerOptions[0].drawerId);
    }
  }, [hierarchyMode, drawerOptions, activeHierarchyDrawerId]);

  const textsInActiveDrawer = React.useMemo(() => {
    if (!activeHierarchyDrawerId) return [];
    return filteredTexts.filter((t) => t.drawerId === activeHierarchyDrawerId);
  }, [filteredTexts, activeHierarchyDrawerId]);

  const notebooksInActiveDrawer = React.useMemo(() => {
    const map = new Map();
    for (const t of textsInActiveDrawer) {
      if (!map.has(t.notebookId)) {
        map.set(t.notebookId, {
          notebookId: t.notebookId,
          notebookName: t.notebookName,
          notebookColor: t.notebookColor,
          deskId: t.deskId,
          drawerId: t.drawerId,
        });
      }
    }
    const arr = [...map.values()];
    arr.sort((a, b) => (a.notebookName || "").localeCompare(b.notebookName || ""));
    return arr;
  }, [textsInActiveDrawer]);

  const textsByNotebookId = React.useMemo(() => {
    const map = {};
    for (const t of textsInActiveDrawer) {
      if (!map[t.notebookId]) map[t.notebookId] = [];
      map[t.notebookId].push(t);
    }
    for (const ids of Object.values(map)) {
      ids.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return map;
  }, [textsInActiveDrawer]);

  const notebookMetaById = React.useMemo(() => {
    const map = {};
    for (const nb of notebooksInActiveDrawer) {
      map[nb.notebookId] = { deskId: nb.deskId, drawerId: nb.drawerId, notebookId: nb.notebookId };
    }
    return map;
  }, [notebooksInActiveDrawer]);

  const handleHierarchyDragEnd = React.useCallback(
    async (result) => {
      try {
        const { destination, source, draggableId } = result || {};
        if (!destination) return;
        if (locked) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const srcNotebookId = String(source.droppableId).replace(/^nb:/, "");
        const destNotebookId = String(destination.droppableId).replace(/^nb:/, "");
        const textId = String(draggableId).replace(/^t:/, "");
        if (!srcNotebookId || !destNotebookId || !textId) return;

        const srcMeta = notebookMetaById[srcNotebookId];
        const destMeta = notebookMetaById[destNotebookId];
        if (!srcMeta || !destMeta) return;

        const srcArr = textsByNotebookId[srcNotebookId] || [];
        const destArr = textsByNotebookId[destNotebookId] || [];
        const srcIds = srcArr.map((t) => t.id);
        const destIdsBase = destArr.map((t) => t.id);

        if (srcNotebookId === destNotebookId) {
          const next = [...srcIds];
          const [moved] = next.splice(source.index, 1);
          next.splice(destination.index, 0, moved);
          await reorderTexts(srcMeta.deskId, srcMeta.drawerId, srcMeta.notebookId, next);
          return;
        }

        await moveText(
          srcMeta.deskId,
          srcMeta.drawerId,
          srcMeta.notebookId,
          textId,
          destMeta.deskId,
          destMeta.drawerId,
          destMeta.notebookId,
        );

        const nextSrc = srcIds.filter((id) => id !== textId);
        const nextDest = destIdsBase.filter((id) => id !== textId);
        nextDest.splice(destination.index, 0, textId);

        await reorderTexts(srcMeta.deskId, srcMeta.drawerId, srcMeta.notebookId, nextSrc);
        await reorderTexts(destMeta.deskId, destMeta.drawerId, destMeta.notebookId, nextDest);
      } catch (e) {
        console.error("[VisionBoard] hierarchy DnD failed:", e);
      }
    },
    [locked, notebookMetaById, textsByNotebookId]
  );

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") navigate("/");
  }, [navigate]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const buildLayout = () => {
    const cloudLayout = settings.corkboardLayout || [];
    const savedMap = new Map(cloudLayout.map((item) => [item.i, item]));
    const layout = [];

    filteredTexts.forEach((t) => {
      if (savedMap.has(t.id)) {
        layout.push({ ...savedMap.get(t.id), i: t.id });
      } else {
        const w = 3, h = 3, cols = 12;
        let x = 0, y = 0;
        let placed = false;
        function checkOverlap(ax, ay, aw, ah) {
          return layout.some(
            (l) =>
              l.x < ax + aw &&
              l.x + l.w > ax &&
              l.y < ay + ah &&
              l.y + l.h > ay
          );
        }
        while (!placed) {
          if (!checkOverlap(x, y, w, h)) {
            layout.push({ i: t.id, x, y, w, h });
            placed = true;
          } else {
            x += w;
            if (x + w > cols) {
              x = 0;
              y += h;
            }
          }
        }
      }
    });

    return layout;
  };

  const handleLayoutChange = (newLayout) => {
    if (locked) return;
    saveCorkboardLayout(newLayout);
  };

  const layout = buildLayout();

  return (
    <HomeShell background={BACKGROUNDS.dashboard} fullHeight={false}>
    <div
      style={{
        position: "relative",
        minHeight: "calc(100vh - 64px)",
        overflow: "auto",
        padding: "40px 20px",
      }}
    >
      {/* Header with Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', maxWidth: '1200px', margin: '0 auto 30px' }}>
        <div>
          <h1 style={glass.greeting}>Vision Board</h1>
          <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
            {[
              { key: "all", label: "Todas" },
              { key: "actionable", label: "Accionables" },
              { key: "pinned", label: "Fijadas" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilterMode(f.key)}
                style={{
                  ...glass.glassPill,
                  ...(filterMode === f.key ? glass.glassPillActive : {}),
                  padding: "6px 16px",
                  fontSize: "0.8rem",
                  fontWeight: "bold",
                }}
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setHierarchyMode((v) => !v)}
              style={{
                ...glass.glassPill,
                ...(hierarchyMode ? glass.glassPillActive : {}),
                padding: "6px 16px",
                fontSize: "0.8rem",
                fontWeight: "bold",
              }}
              title="Jerarquía (cajón → cuadernos)"
            >
              Jerarquía
            </button>
          </div>
        </div>
      </div>

      {hierarchyMode ? (
        <>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, maxWidth: 1200, margin: "0 auto 14px" }}>
            <span style={{ ...glass.subtext, margin: 0 }}>Cajón:</span>
            <select
              value={activeHierarchyDrawerId || ""}
              onChange={(e) => setActiveHierarchyDrawerId(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#fff",
                borderRadius: 12,
                padding: "10px 12px",
                outline: "none",
              }}
              disabled={!drawerOptions.length}
            >
              {drawerOptions.map((d) => (
                <option key={d.drawerId} value={d.drawerId} style={{ background: "#0b0b0b" }}>
                  {d.drawerName}
                </option>
              ))}
            </select>
          </div>

          <DragDropContext onDragEnd={handleHierarchyDragEnd}>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start", overflowX: "auto", paddingBottom: 10, maxWidth: 1200, margin: "0 auto" }}>
              {notebooksInActiveDrawer.map((nb) => {
                const texts = textsByNotebookId[nb.notebookId] || [];
                return (
                  <div key={nb.notebookId} style={{ minWidth: 280, flex: "0 0 auto", display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ ...glass.glassHeader, borderRadius: 14, padding: "10px 12px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.18)" }}>
                      <span style={{ color: nb.notebookColor || "#D4AF37", fontWeight: 900, letterSpacing: "0.7px", textTransform: "uppercase", fontSize: "0.8rem" }}>
                        {nb.notebookName}
                      </span>
                      <span style={{ marginLeft: "auto", color: "rgba(255,255,255,0.55)", fontSize: "0.7rem" }}>{texts.length}</span>
                    </div>

                    <Droppable droppableId={`nb:${nb.notebookId}`} type="text">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 80 }}>
                          {texts.map((t, idx) => (
                            <Draggable key={t.id} draggableId={`t:${t.id}`} index={idx} isDragDisabled={locked}>
                              {(dragProvided, snapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  style={{
                                    ...(dragProvided.draggableProps.style || {}),
                                    opacity: snapshot.isDragging ? 0.96 : 1,
                                  }}
                                >
                                  <div
                                    style={{
                                      ...glass.glassCard,
                                      borderTop: `4px solid ${t.notebookColor || "#D4AF37"}`,
                                      color: "#fff",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "8px",
                                      padding: 0,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      className="drag-handle"
                                      {...dragProvided.dragHandleProps}
                                      style={{
                                        cursor: locked ? "not-allowed" : "move",
                                        padding: "10px 12px",
                                        borderBottom: "1px solid rgba(255,255,255,0.06)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        userSelect: "none",
                                        opacity: locked ? 0.35 : 1,
                                      }}
                                    >
                                      <span style={{ fontSize: "0.75rem", opacity: 0.85, textTransform: "uppercase", fontWeight: "bold", letterSpacing: "1px" }}>
                                        {t.title || "Sin título"}
                                      </span>
                                      <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "1rem" }}>⋮⋮</span>
                                    </div>

                                    <div
                                      style={{ padding: "10px 12px", cursor: "pointer" }}
                                      onClick={() => {
                                        if (locked) return;
                                        rememberWritingText(t);
                                        navigateToText(navigate, t);
                                      }}
                                    >
                                      <div style={{ fontSize: "1rem", fontWeight: 900, fontFamily: "'Playfair Display', serif" }}>
                                        {t.title || "Sin título"}
                                      </div>
                                      <div style={{ marginTop: 6, fontSize: "0.85rem", opacity: 0.85, lineHeight: 1.5 }}>
                                        {(t.content || "").replace(/<[^>]*>?/gm, "").slice(0, 140)}...
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        </>
      ) : (
        <ResponsiveGridLayout
          className="layout"
          layouts={{ lg: layout }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={140}
          draggableHandle=".drag-handle"
          useCSSTransforms={true}
          compactType={null}
          isDraggable={!locked}
          isResizable={!locked}
          onLayoutChange={handleLayoutChange}
        >
          {filteredTexts.map((t) => (
            <div
              key={t.id}
              onClick={() => {
                rememberWritingText(t);
                navigateToText(navigate, t);
              }}
              style={{
                ...glass.glassCard,
                borderTop: `4px solid ${t.notebookColor || "#D4AF37"}`,
                color: "#fff",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                height: "100%",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            >
              <div
                className="drag-handle"
                style={{
                  cursor: locked ? "not-allowed" : "move",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid rgba(255,255,255,0.2)",
                  paddingBottom: "10px",
                  opacity: locked ? 0.35 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="drag-handle-grip" style={{ opacity: 0.6 }}>
                    <div className="grip-dot" /><div className="grip-dot" />
                    <div className="grip-dot" /><div className="grip-dot" />
                  </div>
                  <span style={{ fontSize: "0.8rem", opacity: 0.8, textTransform: "uppercase", fontWeight: 'bold', letterSpacing: '1px' }}>
                    {t.notebookName}
                  </span>
                </div>
              </div>
              <div style={{ fontSize: "1.25rem", fontWeight: "bold", lineHeight: 1.3, fontFamily: "'Playfair Display', serif" }}>
                {t.title}
              </div>
              <div style={{ fontSize: "0.95rem", opacity: 0.9, lineHeight: 1.5, marginTop: "auto", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical" }}>
                {(t.content || "").replace(/<[^>]*>?/gm, "").slice(0, 250)}...
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      <div
        onClick={() => navigate("/dashboard")}
        style={{
          position: "fixed",
          top: "30px",
          right: "30px",
          width: "56px",
          height: "56px",
          borderRadius: "50%",
          backgroundColor: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(12px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#D4AF37",
          fontSize: "2rem",
          border: "2px solid rgba(212,175,55,0.4)",
          zIndex: 10000,
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.15)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.4)'; }}
        title="Salir (Esc)"
      >
        ×
      </div>
    </div>
    </HomeShell>
  );
}
